import React from "react";
import PropTypes from "prop-types";

import { Text, Box, Flex } from "rebass";

// ------------------------------------------------------------------------------------------------

import { HeadingLabel, NormalLabel } from "@sisuwellness/ui/src/components/Labels";
import { SectionHeaders } from "views/HeartAge/commonElements";
import CardStack from "components/Cards/CardStack";
import CompositionGraph from "./compositionGraph";
import CompositionTabs from "./compositionTabs";
import ProgressView from "components/ProgressView";
import { useUserHealthCheckData } from "views/BodyComposition";
import { BODY_FAT, MUSCLE_MASS } from "constants/trend-charts";
import { ModalPrimaryButton } from "components/CommonComponents/Button";
import { useHistory } from "react-router";
import routes from "constants/routes";
import { useTranslation } from "react-i18next";

// ------------------------------------------------------------------------------------------------

const BCompBreakdown = props => {
    const { t } = useTranslation();
    const { latestHealthCheck } = useUserHealthCheckData();
    const history = useHistory();

    const onViewFullHistoryClick = route => history.push(route);

    return (
        <Box>
            <SectionHeaders height="43px" width="100%" px="16px" {...props}>
                <Text color="white" fontSize={["22px", "", "28px"]}>
                    {t("members_portal_web.views.body_composition.unlocked.breakdown.title")}
                </Text>
            </SectionHeaders>

            <HeadingLabel fontSize="22px" mt="24px">
                {t("words.your_body_composition")}
            </HeadingLabel>
            <NormalLabel fontSize="15px" fontWeight="300" mt="8px">
                {t("members_portal_web.views.body_composition.unlocked.breakdown.composition.analysis")}
            </NormalLabel>

            <CardStack gap={["16px", "14px"]} pt="16px">
                <CompositionGraph value={latestHealthCheck} flexBasis={["330px", "40%"]} flexGrow={1} />
                <CompositionTabs flexBasis={["330px", "40%"]} flexGrow={1} />
            </CardStack>

            <HeadingLabel fontSize="22px" mt="24px">
                {t("members_portal_web.views.body_composition.unlocked.breakdown.relevant_muscle.heading")}
            </HeadingLabel>
            <NormalLabel fontSize="15px" fontWeight="300" mt="8px" mb="16px">
                {t("members_portal_web.views.body_composition.unlocked.breakdown.relevant_muscle.compare")}
            </NormalLabel>
            <Flex flexDirection="column" bg="hpCardBackground" px={["22px", "16px"]} py={["24px", "28.5px"]}>
                <ProgressView type={MUSCLE_MASS} datum={latestHealthCheck} showPopup={false} />
                <Flex
                    alignItems="center"
                    px={["0px", "24px"]}
                    mt={["40px", "48px"]}
                    justifyContent="space-between"
                    flexDirection={["column", "row"]}
                >
                    <Text fontSize={["13px", "15px"]} color="hpGrayishBlueText">
                        {t("members_portal_web.views.body_composition.unlocked.breakdown.relevant_muscle.changes")}
                    </Text>
                    <ModalPrimaryButton
                        mt={["24px", "0px"]}
                        height={["40px", "50px"]}
                        width={["235px", "250px"]}
                        fontSize={["14px", "16px"]}
                        onClick={() => onViewFullHistoryClick(routes.muscleMass)}
                    >
                        {t("members_portal_web.views.body_composition.unlocked.breakdown.relevant_muscle.full_history")}
                    </ModalPrimaryButton>
                </Flex>
            </Flex>

            <HeadingLabel fontSize="22px" mt="24px">
                {t("members_portal_web.views.body_composition.unlocked.breakdown.relevant_fat.heading")}
            </HeadingLabel>
            <NormalLabel fontSize="15px" fontWeight="300" mt="8px" mb="16px">
                {t("members_portal_web.views.body_composition.unlocked.breakdown.relevant_fat.compare")}
            </NormalLabel>
            <Flex flexDirection="column" bg="hpCardBackground" px={["22px", "16px"]} py={["24px", "28.5px"]}>
                <ProgressView type={BODY_FAT} datum={latestHealthCheck} showPopup={false} />
                <Flex
                    alignItems="center"
                    px={["0px", "24px"]}
                    mt={["40px", "48px"]}
                    justifyContent="space-between"
                    flexDirection={["column", "row"]}
                >
                    <Text fontSize={["13px", "15px"]} color="hpGrayishBlueText">
                        {t("members_portal_web.views.body_composition.unlocked.breakdown.relevant_fat.changes")}
                    </Text>
                    <ModalPrimaryButton
                        mt={["24px", "0px"]}
                        height={["40px", "50px"]}
                        width={["235px", "250px"]}
                        fontSize={["14px", "16px"]}
                        onClick={() => onViewFullHistoryClick(routes.trend.bodyFat)}
                    >
                        {t("members_portal_web.views.body_composition.unlocked.breakdown.full_history")}
                    </ModalPrimaryButton>
                </Flex>
            </Flex>
        </Box>
    );
};

BCompBreakdown.propTypes = { setWeightGoal: PropTypes.func };

export default BCompBreakdown;
